const HTTP_STATUS_OK = 200
const HTTP_STATUS_CREATED = 201
const HTTP_STATUS_NOT_FOUND = 404

/**
 * Service class for interacting with the custom objects API.
 */
export class CustomObjectApiService {
    /**
     * Get custom objects from the API associated with the user.
     * @async
     * @param {string} lrToken - The LoginRadius token.
     * @returns {Promise<GetCustomObjectApiSuccessResponse>} - A promise that resolves with the response data.
     * @throws {Promise<ApiErrorResponse>} - The error response from the API if the response status is not 200.
     */
    public async getCustomObjects(
        lrToken: string
    ): Promise<GetCustomObjectApiSuccessResponse | ApiErrorResponse> {
        const myHeaders = new Headers()
        myHeaders.append('X-API-U-Token', lrToken)

        const requestOptions = {
            method: 'GET',
            headers: myHeaders
        }

        const response = await fetch(
            '/api-root/subscription/identity/custom-objects/default/',
            requestOptions
        )
        const data = await response.json()

        if (response.ok) {
            return data as GetCustomObjectApiSuccessResponse
        } else if (response.status === HTTP_STATUS_NOT_FOUND) {
            // If the user has no custom objects, the API returns a 404.
            // Return an empty object instead.
            return {
                data: {
                    SavedArticles: {},
                    object_id: undefined,
                    object_modified_on: undefined
                }
            } as GetCustomObjectApiSuccessResponse
        } else {
            throw data as ApiErrorResponse
        }
    }

    /**
     * Save an article to custom objects associated with the user.
     * @async
     * @param {string} lrToken - The LoginRadius token.
     * @param {string} articleId - The ID of the article.
     * @param {string} [objectId] - The ID of the object.
     *  If not provided, a new object will be created.
     *  If provided, the article will be added to the existing object.
     *  If the object_id available in the GET response, it can be used here.
     * @returns {Promise<SaveArticleApiSuccessResponse>} - A promise that resolves with the response data.
     * @throws {Promise<ApiErrorResponse>} - The error response from the API if the response status is not 200 or 201.
     */
    public async saveArticle(
        lrToken: string,
        articleId: string,
        objectId?: string
    ): Promise<SaveArticleApiSuccessResponse | ApiErrorResponse> {
        const myHeaders = new Headers()
        myHeaders.append('X-API-U-Token', lrToken)
        myHeaders.append('Content-Type', 'application/json')

        const raw = JSON.stringify({
            article_id: articleId,
            object_id: objectId
        })

        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: raw
        }

        const response = await fetch(
            '/api-root/subscription/identity/custom-objects/default/saved-articles/',
            requestOptions
        )
        const data = await response.json()

        if (
            response.status === HTTP_STATUS_OK ||
            response.status === HTTP_STATUS_CREATED
        ) {
            return data as SaveArticleApiSuccessResponse
        } else {
            throw data as ApiErrorResponse
        }
    }

    /**
     * Remove a saved article from custom objects associated with the user.
     * @async
     * @param {string} lrToken - The LoginRadius token.
     * @param {string} articleId - The ID of the article to be removed.
     * @param {string} objectId - The ID of the object.
     * @param {string} objectModifiedOn - The last modification date of the object for the user.
     *    Format: 2024-01-09T06:08:13.998Z. Helps to make sure that the object is not modified by another request.
     * @returns {Promise<RemoveSavedArticleApiSuccessResponse>} - A promise that resolves with the response data.
     * @throws {Promise<ApiErrorResponse>} - The error response from the API if the response status is not 200.
     */
    public async removeSavedArticle(
        lrToken: string,
        articleId: string,
        objectId: string,
        objectModifiedOn: string
    ): Promise<RemoveSavedArticleApiSuccessResponse | ApiErrorResponse> {
        const myHeaders = new Headers()
        myHeaders.append('X-API-U-Token', lrToken)
        myHeaders.append('Content-Type', 'application/json')

        const raw = JSON.stringify({
            object_id: objectId,
            object_modified_on: objectModifiedOn
        })

        const requestOptions = {
            method: 'DELETE',
            headers: myHeaders,
            body: raw
        }

        const response = await fetch(
            `/api-root/subscription/identity/custom-objects/default/saved-articles/${articleId}/`,
            requestOptions
        )
        const data = await response.json()

        if (response.ok) {
            return data as RemoveSavedArticleApiSuccessResponse
        } else {
            throw data as ApiErrorResponse
        }
    }
}
